import "./PhoneInputField.scss";

import clsx from "clsx";
import { useState } from "react";
import { useMemo } from "react";
import {
  Control,
  Controller,
  FieldErrors,
  FieldValues,
  Path,
} from "react-hook-form";
import { useTranslation } from "react-i18next";
import {
  defaultCountries,
  guessCountryByPartialPhoneNumber,
  PhoneInput,
  removeDialCode,
} from "react-international-phone";

import {
  ErrorMessage,
  getErrorMessageId,
} from "@/components/Interface/FormFields/ErrorMessage/ErrorMessage";
import { countries as appCountries } from "@/constants/countries";
import { getNestedValue } from "@/utils/objectUtils";

type PhoneInputFieldProps<TFormValues extends FieldValues> = {
  id: string;
  label: string;
  name: Path<TFormValues>;
  control: Control<TFormValues>;
  errors: FieldErrors;
  disabled?: boolean;
  readOnly?: boolean;
  required?: string | boolean;
  validationMessage?: string;
  backendError?: string;
};

export const PhoneInputField = <TFormValues extends FieldValues>({
  id,
  label,
  name,
  control,
  errors,
  disabled = false,
  readOnly = false,
  required,
  validationMessage,
  backendError,
}: PhoneInputFieldProps<TFormValues>) => {
  const { t } = useTranslation();
  const [isFocused, setIsFocused] = useState(false);

  const translatedCountries = useMemo(() => {
    const iso2ToAlpha3 = Object.fromEntries(
      appCountries.map((c) => [c.ALPHA2.toLowerCase(), c.ALPHA3]),
    );

    return defaultCountries.map((entry) => {
      const alpha3 = iso2ToAlpha3[entry[1] as string];
      if (!alpha3) {
        return entry;
      }

      return [
        t(`countries.${alpha3}`, { defaultValue: entry[0] }),
        ...entry.slice(1),
      ] as typeof entry;
    });
  }, [t]);

  const fieldError = getNestedValue(errors, name);
  const hasError = fieldError !== undefined;

  const rawMessage = fieldError?.message as string | undefined;
  const errorMessage =
    rawMessage ||
    (fieldError?.type === "backendError" ? backendError : undefined) ||
    "";

  const isOptional = !required && !disabled && !readOnly;
  const optionalLabel = t("translation:Common.label.optional");
  const labelWithOptional = isOptional ? `${label} ${optionalLabel}` : label;

  return (
    <div className="form-field-container phone-input-container">
      <Controller
        name={name}
        control={control}
        rules={{
          validate: (value: unknown) => {
            const phone = (value as string) || "";
            const stripped = phone.replace(/[\s\-()/]/g, "");

            if (!stripped) {
              return typeof required === "string"
                ? required
                : String(required || "");
            }

            if (!/^\+[1-9]\d+$/.test(stripped) || stripped.length < 7) {
              return validationMessage || String(required || "");
            }

            return true;
          },
        }}
        render={({ field }) => {
          const handleChange = (
            newPhone: string,
            meta: { country: { dialCode: string } },
          ) => {
            const newDialCode = "+" + meta.country.dialCode;
            const prevPhone = (field.value as string) || "";

            // When only the country is switched the library emits just the new
            // dial code (e.g. "+49"). Detect this and reattach the national
            // number by asking the library to identify the previous country.
            if (
              newPhone === newDialCode &&
              prevPhone &&
              prevPhone !== newDialCode
            ) {
              const { country: prevCountry } = guessCountryByPartialPhoneNumber(
                {
                  phone: prevPhone,
                  countries: defaultCountries,
                },
              );

              if (prevCountry) {
                const nationalNumber = removeDialCode({
                  phone: prevPhone,
                  dialCode: prevCountry.dialCode,
                });

                if (nationalNumber) {
                  field.onChange(newDialCode + nationalNumber);

                  return;
                }
              }
            }

            field.onChange(newPhone);
          };

          // The field's value always contains at least the dial code (e.g. "+41"),
          // so plain truthiness can't tell "empty" from "just the dial code" —
          // strip the dial code and check what's actually left.
          const currentPhone = (field.value as string) ?? "";
          const { country } = guessCountryByPartialPhoneNumber({
            phone: currentPhone,
            countries: defaultCountries,
          });
          const hasValue = !!(
            country &&
            removeDialCode({
              phone: currentPhone,
              dialCode: country.dialCode,
            }).trim()
          );
          const isFloating = isFocused || hasValue;

          return (
            <div className="phone-input-wrapper">
              <PhoneInput
                className={clsx(
                  hasError && "error",
                  (disabled || readOnly) && "disabled",
                )}
                countries={translatedCountries}
                inputClassName={clsx(hasValue && "has-value")}
                inputProps={{
                  id,
                  // @ts-expect-error data-testid is a valid HTML attribute
                  "data-testid": String(name),
                  "aria-required": !!required,
                  "aria-invalid": hasError ? "true" : "false",
                  "aria-describedby": hasError
                    ? getErrorMessageId(String(name))
                    : undefined,
                }}
                value={currentPhone}
                defaultCountry="ch"
                onFocus={() => setIsFocused(true)}
                onBlur={() => {
                  setIsFocused(false);
                  field.onBlur();
                }}
                disabled={disabled || readOnly}
                onChange={handleChange}
              />
              <label
                className={clsx(
                  "phone-input-label",
                  isFloating && "floating",
                  hasError && "error",
                  (disabled || readOnly) && "disabled",
                )}
                htmlFor={id}
              >
                {labelWithOptional}
              </label>
            </div>
          );
        }}
      />
      {errorMessage && (
        <ErrorMessage message={errorMessage} name={String(name)} />
      )}
    </div>
  );
};
